import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Laboratory as LabType } from '@/types/product';
import { db, COLLECTIONS } from '@/lib/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useToast } from "@/components/ui/use-toast";
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Helmet } from 'react-helmet-async';

export default function Laboratory() {
  const { id } = useParams<{ id: string }>();
  const [laboratory, setLaboratory] = useState<LabType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const labName = laboratory?.name || 'Laboratoire médical';
  const labDescription = laboratory?.description || 'Détail d’un laboratoire médical en Mauritanie et Afrique du Nord.';

  useEffect(() => {
    const fetchLaboratory = async () => {
      try {
        if (!id) return;
        
        const docRef = doc(db, COLLECTIONS.LABORATORIES, id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setLaboratory({
            id: docSnap.id,
            ...docSnap.data()
          } as LabType);
        } else {
          toast({
            title: "Erreur",
            description: "Laboratoire non trouvé",
            variant: "destructive"
          });
        }
      } catch (error) {
        console.error('Error fetching laboratory:', error);
        toast({
          title: "Erreur",
          description: "Impossible de charger les informations du laboratoire",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchLaboratory();
  }, [id, toast]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!laboratory) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">Laboratoire non trouvé</h1>
            <p className="mt-2 text-gray-600">Le laboratoire que vous recherchez n'existe pas.</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{labName} | Laboratoire partenaire | LeaderProMed</title>
        <meta name="description" content={labDescription} />
        <meta property="og:title" content={labName + ' | Laboratoire partenaire | LeaderProMed'} />
        <meta property="og:description" content={labDescription} />
        <meta property="og:type" content="profile" />
      </Helmet>
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="p-8">
                <div className="flex items-center gap-8">
                  {laboratory.logo && (
                    <img 
                      src={laboratory.logo} 
                      alt={laboratory.name}
                      className="w-32 h-32 object-contain"
                    />
                  )}
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900">{laboratory.name}</h1>
                    {laboratory.website && (
                      <a 
                        href={laboratory.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline mt-2 inline-block"
                      >
                        Visiter le site web
                      </a>
                    )}
                  </div>
                </div>

                <div className="mt-8">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">À propos du laboratoire</h2>
                  <p className="text-gray-600 leading-relaxed">{laboratory.description}</p>
                </div>

                {/* Ici, vous pouvez ajouter d'autres sections comme :
                    - Liste des produits du laboratoire
                    - Actualités liées au laboratoire
                    - Coordonnées de contact
                    - etc. */}
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
} 